import { useId } from 'react'
import { cn } from '@/lib/cn'
import { todayManila, addDays, formatDate } from '@/lib/format'

interface DateRangePickerProps {
  checkIn: string
  checkOut: string
  onChange: (checkIn: string, checkOut: string) => void
  className?: string
  /** When true renders as compact inline fields (used inside SearchBar) */
  inline?: boolean
}

export function DateRangePicker({
  checkIn,
  checkOut,
  onChange,
  className,
  inline = false,
}: DateRangePickerProps) {
  const inId  = useId()
  const outId = useId()
  const today = todayManila()

  function handleCheckInChange(val: string) {
    // If new check-in is same or after check-out, push check-out forward by 1 day
    const newOut = val >= checkOut ? addDays(val, 1) : checkOut
    onChange(val, newOut)
  }

  function handleCheckOutChange(val: string) {
    onChange(checkIn, val)
  }

  const fieldClass = cn(
    'flex flex-col gap-0.5',
    inline ? 'flex-1' : 'w-full',
  )

  const inputClass = cn(
    'h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kasa-green-500 focus-visible:ring-offset-1',
    'cursor-pointer',
  )

  const labelClass = 'text-[10px] font-semibold uppercase tracking-wide text-gray-500'

  if (inline) {
    return (
      <div className={cn('flex items-stretch gap-px', className)}>
        {/* Check-in */}
        <div className={fieldClass}>
          <label htmlFor={inId} className={labelClass}>
            Check-in
          </label>
          <input
            id={inId}
            type="date"
            value={checkIn}
            min={today}
            onChange={(e) => handleCheckInChange(e.target.value)}
            className={cn(inputClass, 'rounded-r-none')}
          />
        </div>

        {/* Divider */}
        <div className="flex items-end pb-0.5">
          <span className="flex h-10 items-center border-y border-gray-300 bg-white px-2 text-gray-300">
            →
          </span>
        </div>

        {/* Check-out */}
        <div className={fieldClass}>
          <label htmlFor={outId} className={labelClass}>
            Check-out
          </label>
          <input
            id={outId}
            type="date"
            value={checkOut}
            min={addDays(checkIn, 1)}
            onChange={(e) => handleCheckOutChange(e.target.value)}
            className={cn(inputClass, 'rounded-l-none')}
          />
        </div>
      </div>
    )
  }

  // Stacked layout for standalone use (e.g. PropertyDetail sidebar)
  return (
    <div className={cn('space-y-3', className)}>
      <div className={fieldClass}>
        <label htmlFor={inId} className={cn(labelClass, 'text-xs normal-case tracking-normal text-gray-700 font-medium')}>
          Check-in
        </label>
        <input
          id={inId}
          type="date"
          value={checkIn}
          min={today}
          onChange={(e) => handleCheckInChange(e.target.value)}
          className={inputClass}
        />
        <span className="text-xs text-gray-500">{formatDate(checkIn)}</span>
      </div>

      <div className={fieldClass}>
        <label htmlFor={outId} className={cn(labelClass, 'text-xs normal-case tracking-normal text-gray-700 font-medium')}>
          Check-out
        </label>
        <input
          id={outId}
          type="date"
          value={checkOut}
          min={addDays(checkIn, 1)}
          onChange={(e) => handleCheckOutChange(e.target.value)}
          className={inputClass}
        />
        <span className="text-xs text-gray-500">{formatDate(checkOut)}</span>
      </div>
    </div>
  )
}
